import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { FieldState } from '@user/components/xGrid'
import { StoreData } from '../reducers/typings'
import {
  loadFilterPreferences,
  loadListViewPreferences,
  saveFilterPreference,
} from './api'
import { IUserPreferenceFilter } from './typings'
import { deserialize } from './utils'

const filterPeferences = createEntityAdapter<IUserPreferenceFilter>()

export const initialState = filterPeferences.getInitialState<{
  listViewPreferencesStatus: 'idle' | 'pending' | 'loaded'
  leadColumnOrder: FieldState[]
  opportunityColumnOrder: FieldState[]
  draftLeadColumnOrder: FieldState[]
  activitySummaryColumnOrder: FieldState[]
  importColumnOrder: FieldState[]
  usersColumnOrder: FieldState[]
  scWebhooksColumnOrder: FieldState[]
  rolesColumnOrder: FieldState[]
  workWaveContactDuplicatesColumnOrder: FieldState[]
  pestPacLocationDuplicatesColumnOrder: FieldState[]
  guestUserDashboardLeadsColumnOrder: FieldState[]
  guestUserDashboardOpportunitiesColumnOrder: FieldState[]
}>({
  listViewPreferencesStatus: 'idle',
  leadColumnOrder: [],
  opportunityColumnOrder: [],
  draftLeadColumnOrder: [],
  activitySummaryColumnOrder: [],
  importColumnOrder: [],
  usersColumnOrder: [],
  scWebhooksColumnOrder: [],
  rolesColumnOrder: [],
  workWaveContactDuplicatesColumnOrder: [],
  pestPacLocationDuplicatesColumnOrder: [],
  guestUserDashboardLeadsColumnOrder: [],
  guestUserDashboardOpportunitiesColumnOrder: [],
})

export const userPreferences = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    setDraftLeadColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.draftLeadColumnOrder = payload
    },
    setLeadColumnOrder: (state, { payload }: PayloadAction<FieldState[]>) => {
      state.leadColumnOrder = payload
    },
    setOpportunityColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.opportunityColumnOrder = payload
    },
    setActivitySummaryColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.activitySummaryColumnOrder = payload
    },
    setImportColumnOrder: (state, { payload }: PayloadAction<FieldState[]>) => {
      state.importColumnOrder = payload
    },
    setUsersColumnOrder: (state, { payload }: PayloadAction<FieldState[]>) => {
      state.usersColumnOrder = payload
    },
    setSCWebhooksColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.scWebhooksColumnOrder = payload
    },
    setRolesColumnOrder: (state, { payload }: PayloadAction<FieldState[]>) => {
      state.rolesColumnOrder = payload
    },
    setWorkWaveContactsColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.workWaveContactDuplicatesColumnOrder = payload
    },
    setPestPacLocationColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.pestPacLocationDuplicatesColumnOrder = payload
    },
    setGuestUserDashboardLeadsColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.guestUserDashboardLeadsColumnOrder = payload
    },
    setGuestUserDashboardOpportunitiesColumnOrder: (
      state,
      { payload }: PayloadAction<FieldState[]>,
    ) => {
      state.guestUserDashboardOpportunitiesColumnOrder = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      filterPeferences.setAll(state, payload)
    })
    builder.addCase(saveFilterPreference.fulfilled, (state, { payload }) => {
      filterPeferences.upsertOne(state, payload)
    })
    builder.addCase(loadListViewPreferences.fulfilled, (state, { payload }) => {
      state.listViewPreferencesStatus = 'loaded'
      payload.map((pref) => {
        const columnOrder = deserialize(pref.columnOrder)
        switch (pref.type) {
          case 'draft':
            state.draftLeadColumnOrder = columnOrder
            break
          case 'lead':
            state.leadColumnOrder = columnOrder
            break
          case 'opp':
            state.opportunityColumnOrder = columnOrder
            break
          case 'activitySummary':
            state.activitySummaryColumnOrder = columnOrder
            break
          case 'import':
            state.importColumnOrder = columnOrder
            break
          case 'users':
            state.usersColumnOrder = columnOrder
            break
          case 'scWebhooks':
            state.scWebhooksColumnOrder = columnOrder
            break
          case 'roles':
            state.rolesColumnOrder = columnOrder
            break
          case 'workWaveContactDuplicates':
            state.workWaveContactDuplicatesColumnOrder = columnOrder
            break
          case 'pestPacLocationDuplicates':
            state.pestPacLocationDuplicatesColumnOrder = columnOrder
            break
          case 'guestUserDashboardLeads':
            state.guestUserDashboardLeadsColumnOrder = columnOrder
            break
          case 'guestUserDashboardOpportunities':
            state.guestUserDashboardOpportunitiesColumnOrder = columnOrder
            break
        }
      })
    })
  },
})

export default userPreferences.reducer

export const {
  setLeadColumnOrder,
  setOpportunityColumnOrder,
  setDraftLeadColumnOrder,
  setActivitySummaryColumnOrder,
  setImportColumnOrder,
  setUsersColumnOrder,
  setSCWebhooksColumnOrder,
  setRolesColumnOrder,
  setWorkWaveContactsColumnOrder,
  setPestPacLocationColumnOrder,
  setGuestUserDashboardLeadsColumnOrder,
  setGuestUserDashboardOpportunitiesColumnOrder,
} = userPreferences.actions

export const { selectAll: selectAllFilterPreferences } =
  filterPeferences.getSelectors((state: StoreData) => state.userPreferences)

export const selectListViewPreferencesStatus = (state: StoreData) =>
  state.userPreferences.listViewPreferencesStatus

export const selectDraftLeadColumnOrder = (state: StoreData) =>
  state.userPreferences.draftLeadColumnOrder

export const selectLeadColumnOrder = (state: StoreData) =>
  state.userPreferences.leadColumnOrder

export const selectOpportunityColumnOrder = (state: StoreData) =>
  state.userPreferences.opportunityColumnOrder

export const selectActivitySummaryColumnOrder = (state: StoreData) =>
  state.userPreferences.activitySummaryColumnOrder

export const selectImportColumnOrder = (state: StoreData) =>
  state.userPreferences.importColumnOrder

export const selectUsersColumnOrder = (state: StoreData) =>
  state.userPreferences.usersColumnOrder

export const selectSCWebhooksColumnOrder = (state: StoreData) =>
  state.userPreferences.scWebhooksColumnOrder

export const selectRolesColumnOrder = (state: StoreData) =>
  state.userPreferences.rolesColumnOrder

export const selectWorkWaveContactDuplicatesColumnOrder = (state: StoreData) =>
  state.userPreferences.workWaveContactDuplicatesColumnOrder

export const selectPestPacLocationDuplicatesColumnOrder = (state: StoreData) =>
  state.userPreferences.pestPacLocationDuplicatesColumnOrder

export const selectGuestUserDashboardLeadsColumnOrder = (state: StoreData) =>
  state.userPreferences.guestUserDashboardLeadsColumnOrder
export const selectGuestUserDashboardOpportunitiesColumnOrder = (
  state: StoreData,
) => state.userPreferences.guestUserDashboardOpportunitiesColumnOrder
